"use client";

import { useEffect, useState } from "react";
import moment from "moment";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminUserList,
  adminUserSelectStateUpdate,
  UserProvider,
  UserRole,
  UserState,
} from "@/entities";
import {
  AdminPagination,
  PanelLoader,
  Panel,
  PanelHeader,
  PanelBody,
  PanelError,
  getFileUrl,
} from "@/shared";
import { AdminUsersButtons, AdminUsersFilter } from "@/features";
import { toast } from "react-toastify";

export function AdminUserList() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: adminUserSelectStateUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`admin-users`, { ...filterParams, page, perPage }],
      });
      toast.success("회원 상태가 변경되었습니다.");

      const checkers = document.getElementsByName(
        "ids"
      ) as NodeListOf<HTMLInputElement>;
      checkers.forEach((checker) => (checker.checked = false));
      setIds([]);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });

  const provider = searchParams?.get("provider") || "";
  const role = searchParams?.get("role") || "";
  const state = searchParams?.get("state") || "";
  const keyword = searchParams?.get("keyword") || "";
  const page = parseInt(searchParams?.get("page") || "1");

  const perPage = 15;
  const filterParams = { provider, role, state, keyword };

  const { error, isLoading, data } = useQuery({
    queryKey: [`admin-users`, { ...filterParams, page, perPage }],
    queryFn: () => adminUserList({ ...filterParams, page, perPage }),
  });

  const [ids, setIds] = useState([] as string[]);
  const doAllChecker = (e: any) => {
    const checked = e.target.checked;

    const checkers = document.getElementsByName(
      "ids"
    ) as NodeListOf<HTMLInputElement>;

    checkers.forEach((checker) => {
      checker.checked = checked ? true : false;
    });

    if (checked) {
      const all = data?.result.map((item) => item.id.toString());
      setIds(all ?? []);
    } else {
      setIds([]);
    }
  };
  const doChecker = (e: any) => {
    const checked = e.target.checked;
    const value = e.target.value;

    if (checked) {
      setIds([...ids, value]);
    } else {
      setIds(ids.filter((id) => id !== value));
    }
  };
  const doActive = () => {
    // console.log(ids);
    if (!confirm("활성화 하시겠습니까?")) return;
    if (updateMutation.isPending) return;
    updateMutation.mutate({ ids, state: "active" });
  };
  const doInactive = () => {
    // console.log(ids);
    if (!confirm("차단 하시겠습니까?")) return;
    if (updateMutation.isPending) return;
    updateMutation.mutate({ ids, state: "inactive" });
  };
  useEffect(() => {
    const allChecker = document.getElementById(
      "allChecker"
    ) as HTMLInputElement;

    if (data?.result && data?.result.length > 0) {
      if (data?.result.length === ids.length) {
        allChecker.checked = true;
      } else {
        allChecker.checked = false;
      }
    }
  }, [ids]);

  if (error) return <PanelError error={error} />;
  if (isLoading) return <PanelLoader panelTitle={`목록(0건)`} />;

  return (
    <>
      {/* <UsersAlert /> */}
      <AdminUsersButtons />

      <Panel queryKey={[`admin-users`, { ...filterParams, page, perPage }]}>
        <PanelHeader>목록{!isLoading && `(${data?.count}건)`}</PanelHeader>
        <PanelBody>
          <AdminUsersFilter />

          {data?.count && data?.count > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle text-center text-nowrap">
                  <colgroup>
                    <col width="5%" />
                    <col width="5%" />
                    <col width="5%" />
                    <col width="5%" />
                    <col width="50%" />
                    <col width="20%" />
                    <col width="10%" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        className="text-left align-middle"
                        colSpan={ids.length > 0 ? 7 : 1}
                      >
                        <span className="form-check">
                          <input
                            type="checkbox"
                            id="allChecker"
                            className="form-check-input"
                            onChange={doAllChecker}
                          />
                          {ids.length > 0 && (
                            <span className="form-check-label">
                              <button
                                type="button"
                                className="mx-2 disabled:opacity-50"
                                onClick={doActive}
                                disabled={updateMutation.isPending}
                              >
                                활성
                              </button>
                              <button
                                type="button"
                                className="mx-2 disabled:opacity-50"
                                onClick={doInactive}
                                disabled={updateMutation.isPending}
                              >
                                차단
                              </button>
                            </span>
                          )}
                        </span>
                      </th>
                      <th className={ids.length > 0 ? "hide" : ""}>가입</th>
                      <th className={ids.length > 0 ? "hide" : ""}>권한</th>
                      <th className={ids.length > 0 ? "hide" : ""}>상태</th>
                      <th className={ids.length > 0 ? "hide" : ""}>
                        회원 정보
                      </th>
                      <th className={ids.length > 0 ? "hide" : ""}>
                        최근 로그인일시
                      </th>
                      <th className={ids.length > 0 ? "hide" : ""}>가입일시</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.result.map((item: any, index: number) => {
                      const link = `/admin/users/${
                        item.id
                      }?${searchParams?.toString()}`;
                      return (
                        <tr key={index}>
                          <td className="text-left">
                            <input
                              type="checkbox"
                              name="ids"
                              className="form-check-input"
                              onChange={doChecker}
                              value={item.id}
                            />
                          </td>
                          <td>{UserProvider(item.provider)}</td>
                          <td>{UserRole(item.role)}</td>
                          <td>{UserState(item.state)}</td>
                          <td
                            className="text-start cursor-pointer"
                            onClick={() => router.push(link)}
                          >
                            <div className="d-flex align-items-center">
                              <div className="w-50px h-50px bg-light d-flex align-items-center justify-content-center">
                                <Image
                                  src={
                                    item?.cover?.file_url
                                      ? getFileUrl(item?.cover?.file_url)
                                      : "/assets/images/default_user.png"
                                  }
                                  className="mw-100 mh-100"
                                  width={50}
                                  height={50}
                                  alt="프로필 이미지"
                                />
                              </div>
                              <div className="ms-3">
                                <div className="text-ellipsis">
                                  이름 : {item.name}{" "}
                                  {moment()
                                    .add(-1, "days")
                                    .isBefore(moment(item.created_at)) && (
                                    <span className="badge bg-dark">New</span>
                                  )}
                                </div>
                                <div className="text-ellipsis">
                                  이메일 : {item.email}
                                </div>
                                <div className="text-ellipsis">
                                  휴대번화 : {item.phone}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            {item.login_at
                              ? new Date(item.login_at).toLocaleString("ko")
                              : "-"}
                          </td>
                          <td>
                            {new Date(item.created_at).toLocaleDateString("ko")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="d-md-flex align-items-center justify-content-between">
                <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
                  {data.count}개 항목 중 {(page - 1) * perPage + 1}에서{" "}
                  {data.count < (page - 1) * perPage + perPage
                    ? data.count
                    : (page - 1) * perPage + perPage}
                  까지 표시
                </div>
                <AdminPagination
                  total={Math.ceil(data?.count / perPage)}
                  page={page}
                  pathname="/admin/users"
                  params={filterParams}
                />
              </div>
            </>
          ) : (
            <p className="m-5 text-center">등록된 데이터가 없습니다.</p>
          )}
        </PanelBody>
      </Panel>
    </>
  );
}
